import axios from 'axios';
import { getHeaders } from '../lib/requests';

/**
 * Fetch a specific customer by their ID.
 * @param {string} customerId
 * @returns {Promise<object>}
 */
export async function fetchCustomer(customerId) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const apiToken = localStorage.getItem('accessToken');
    try {
        const response = await axios.get(`${apiUrl}/customers/${customerId}`, {
            headers: getHeaders(apiToken),
        });
        return response.data.data;
    } catch (error) {
        console.error("Error fetching customer:", error);
        throw error;
    }
}

/**
 * Fetch all customers with optional filters.
 * @param {object} filters
 * @returns {Promise<object[]>}
 */
export async function fetchCustomers(filters = {}) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const apiToken = localStorage.getItem('accessToken');
    try {
        const response = await axios.get(`${apiUrl}/customers`, {
            headers: getHeaders(apiToken),
            params: filters,
        });
        return response.data.data;
    } catch (error) {
        console.error("Error fetching customers:", error);
        throw error;
    }
}

/**
 * Create a new customer.
 * @param {object} customerData
 * @returns {Promise<object>}
 */
export async function createCustomer(customerData) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const apiToken = localStorage.getItem('accessToken');
    try {
        const response = await axios.post(`${apiUrl}/customers`, customerData, {
            headers: getHeaders(apiToken),
        });
        return response.data;
    } catch (error) {
        console.error("Error creating customer:", error);
        throw error;
    }
}

/**
 * Update an existing customer's information.
 * @param {string} customerId
 * @param {object} customerData
 * @returns {Promise<object>}
 */
export async function updateCustomer(customerId, customerData) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const apiToken = localStorage.getItem('accessToken');
    try {
        const response = await axios.put(`${apiUrl}/customers/${customerId}`, customerData, {
            headers: getHeaders(apiToken),
        });
        return response.data;
    } catch (error) {
        console.error("Error updating customer:", error);
        throw error;
    }
}

/**
 * Delete a customer by their ID.
 * @param {string} customerId
 * @returns {Promise<void>}
 */
export async function deleteCustomer(customerId) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const apiToken = localStorage.getItem('accessToken');
    try {
        await axios.delete(`${apiUrl}/customers/${customerId}`, {
            headers: getHeaders(apiToken),
        });
    } catch (error) {
        console.error("Error deleting customer:", error);
        throw error;
    }
}

/**
 * Fetch a customer's delivery schedule.
 * @param {string} customerId
 * @returns {Promise<object>}
 */
export async function fetchDeliverySchedule(customerId) {
    const customer = await fetchCustomer(customerId);
    return customer.delivery_schedule;
}

/**
 * Search for customers with a keyword.
 * @param {string} keyword
 * @param {object} additionalFilters
 * @returns {Promise<object[]>}
 */
export async function searchCustomers(keyword, additionalFilters = {}) {
    const filters = {
        search: keyword,
        ...additionalFilters,
    };
    return await fetchCustomers(filters);
}

/**
 * Fetch customers based on their status.
 * @param {string} status
 * @param {object} additionalFilters
 * @returns {Promise<object[]>}
 */
export async function fetchCustomersByStatus(status, additionalFilters = {}) {
    const filters = {
        status,
        ...additionalFilters,
    };
    return await fetchCustomers(filters);
}

/**
 * Fetch customers associated with a specific client.
 * @param {string} clientId
 * @param {object} additionalFilters
 * @returns {Promise<object[]>}
 */
export async function fetchCustomersByClient(clientId, additionalFilters = {}) {
    const filters = {
        client_id: clientId,
        ...additionalFilters,
    };
    return await fetchCustomers(filters);
}
